import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useAppConfig } from '../lib/appConfig';
import { isEpisodeCard, groupEpisodes } from '../lib/longformGroups';
import { cardTitle } from '../lib/cardTitle';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toInputDate(str) {
  if (!str) return '';
  return typeof str === 'string' ? str.slice(0, 10) : new Date(str).toISOString().slice(0, 10);
}

function formatDisplayDate(str) {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Chip / peek / picker title for a slot: episode cards read "Series · Ep N" (D-120); others unchanged. */
function slotTitle(slot) {
  const prod = slot.liveProduction ?? slot.aiProduction;
  return isEpisodeCard(prod) ? cardTitle(prod) : (prod?.script?.scriptName ?? `Slot #${slot.slotOrder}`);
}

// ── Scheduler page ────────────────────────────────────────────────────────────

export default function Scheduler() {
  const { aiProductionEnabled } = useAppConfig();
  const [slots,         setSlots]         = useState([]);
  const [approved,      setApproved]      = useState([]);
  const [clients,       setClients]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [clientFilter,  setClientFilter]  = useState('');
  const [view,          setView]          = useState('queue'); // 'queue' | 'calendar'
  const [removeConfirm, setRemoveConfirm] = useState(null);   // slot to confirm removal

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // With AI Production disabled the backend returns no AI slots; skipping
      // the AI available-list fetch here empties aiAvail, so the AI track
      // column never renders (hasAI is derived from slots + available items).
      const [slotsRes, liveRes, aiRes, clientsRes] = await Promise.all([
        api.get('/api/scheduler'),
        api.get('/api/live-production?status=Approved'),
        aiProductionEnabled ? api.get('/api/ai-production?status=Approved') : Promise.resolve({ data: [] }),
        api.get('/api/clients'),
      ]);
      setSlots(slotsRes.data);
      setApproved([
        ...liveRes.data.map(item => ({ ...item, track: 'Live' })),
        ...aiRes.data.map(item => ({ ...item, track: 'AI' })),
      ]);
      setClients(clientsRes.data);
    } catch {
      setError('Failed to load scheduler data.');
    } finally {
      setLoading(false);
    }
  }, [aiProductionEnabled]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Derived ──────────────────────────────────────────────────────────────────

  // Group slots by clientId, then by track
  const slotsByClient = useMemo(() => {
    const map = {};
    slots.forEach(slot => {
      if (!map[slot.clientId]) map[slot.clientId] = { Live: [], AI: [] };
      const track = slot.track === 'AI' ? 'AI' : 'Live';
      map[slot.clientId][track].push(slot);
    });
    // Sort each track by slotOrder
    Object.values(map).forEach(byTrack => {
      byTrack.Live.sort((a, b) => a.slotOrder - b.slotOrder);
      byTrack.AI.sort((a, b) => a.slotOrder - b.slotOrder);
    });
    return map;
  }, [slots]);

  // Approved items not yet in queue, per clientId + track
  const availableByClientTrack = useMemo(() => {
    const inQueue = new Set();
    slots.forEach(s => {
      if (s.liveProductionId) inQueue.add(`live-${s.liveProductionId}`);
      if (s.aiProductionId)   inQueue.add(`ai-${s.aiProductionId}`);
    });
    const map = {};
    approved.forEach(item => {
      const key = `${item.clientId}-${item.track}`;
      if (!map[key]) map[key] = [];
      const qKey = item.track === 'Live' ? `live-${item.id}` : `ai-${item.id}`;
      if (!inQueue.has(qKey)) map[key].push(item);
    });
    return map;
  }, [slots, approved]);

  // Determine which clients to show: those with slots OR with available approved items
  const visibleClientIds = useMemo(() => {
    const ids = new Set();
    slots.forEach(s => ids.add(s.clientId));
    approved.forEach(a => ids.add(a.clientId));
    return [...ids];
  }, [slots, approved]);

  const filteredClientIds = useMemo(() => {
    if (clientFilter) return visibleClientIds.filter(id => id === clientFilter);
    return visibleClientIds;
  }, [visibleClientIds, clientFilter]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  async function handleDateChange(slot, newDate) {
    const prev = slots;
    setSlots(s => s.map(sl => sl.id === slot.id ? { ...sl, scheduledDate: newDate || null } : sl));
    try {
      await api.patch(`/api/scheduler/${slot.id}`, { scheduledDate: newDate || null });
    } catch {
      setSlots(prev);
      alert('Failed to update date.');
    }
  }

  async function handleMoveUp(slot, trackSlots) {
    const idx = trackSlots.findIndex(s => s.id === slot.id);
    if (idx === 0) return;
    const other = trackSlots[idx - 1];
    const prevSlots = slots;
    setSlots(s => s.map(sl => {
      if (sl.id === slot.id)  return { ...sl, slotOrder: other.slotOrder };
      if (sl.id === other.id) return { ...sl, slotOrder: slot.slotOrder };
      return sl;
    }));
    try {
      await api.post('/api/scheduler/swap', { slotAId: slot.id, slotBId: other.id });
    } catch {
      setSlots(prevSlots);
      alert('Failed to reorder slots.');
    }
  }

  async function handleMoveDown(slot, trackSlots) {
    const idx = trackSlots.findIndex(s => s.id === slot.id);
    if (idx === trackSlots.length - 1) return;
    const other = trackSlots[idx + 1];
    const prevSlots = slots;
    setSlots(s => s.map(sl => {
      if (sl.id === slot.id)  return { ...sl, slotOrder: other.slotOrder };
      if (sl.id === other.id) return { ...sl, slotOrder: slot.slotOrder };
      return sl;
    }));
    try {
      await api.post('/api/scheduler/swap', { slotAId: slot.id, slotBId: other.id });
    } catch {
      setSlots(prevSlots);
      alert('Failed to reorder slots.');
    }
  }

  async function doRemove(slot) {
    const prev = slots;
    setSlots(s => s.filter(sl => sl.id !== slot.id));
    setRemoveConfirm(null);
    try {
      await api.delete(`/api/scheduler/${slot.id}`);
      const res = await api.get('/api/scheduler');
      setSlots(res.data);
    } catch {
      setSlots(prev);
      alert('Failed to remove slot.');
    }
  }

  function handleRemove(slot) {
    setRemoveConfirm(slot);
  }

  async function handleAddItem(clientId, track, item) {
    try {
      const body = { clientId, track };
      if (track === 'Live') body.liveProductionId = item.id;
      else                  body.aiProductionId   = item.id;
      const res = await api.post('/api/scheduler', body);
      setSlots(prev => [...prev, res.data]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add to queue.');
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const totalQueued = slots.length;

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Posting Scheduler</div>
          <div className="page-sub">
            {loading ? 'Loading…' : `${totalQueued} video${totalQueued !== 1 ? 's' : ''} in queue`}
          </div>
        </div>
        <div className="topbar-actions">
          <div style={{ display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
            {['queue', 'calendar'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: view === v ? 600 : 400,
                  background: view === v ? 'var(--violet-soft)' : 'none',
                  color: view === v ? 'var(--violet)' : 'var(--tx-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font)',
                  transition: 'all 0.15s',
                  textTransform: 'capitalize',
                }}
              >
                {v === 'queue' ? '▦ Queue' : '⊞ Calendar'}
              </button>
            ))}
          </div>
          <button className="btn-ghost" onClick={fetchAll}>↺ Refresh</button>
        </div>
      </div>

      <div className="body">

        {/* Client filter */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="filter-input"
            style={{ width: 200 }}
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
          >
            <option value="">All projects</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {clientFilter && (
            <button
              className="btn-ghost"
              style={{ padding: '4px 10px', fontSize: 11 }}
              onClick={() => setClientFilter('')}
            >
              Clear ×
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13, padding: '12px 0' }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ color: 'var(--tx-faint)', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
            Loading…
          </div>
        )}

        {/* Calendar view */}
        {!loading && view === 'calendar' && (
          <SchedulerCalendar
            slots={slots}
            clients={clients}
            clientFilter={clientFilter}
            onDateChange={handleDateChange}
          />
        )}

        {/* Queue (client cards) */}
        {!loading && view === 'queue' && filteredClientIds.map(clientId => {
          const clientObj  = clients.find(c => c.id === clientId);
          const name       = clientObj?.name     ?? 'Unknown Project';
          const initials   = clientObj?.initials ?? name.slice(0, 2).toUpperCase();

          const clientSlots = slotsByClient[clientId] ?? { Live: [], AI: [] };
          const liveSlots   = clientSlots.Live;
          const aiSlots     = clientSlots.AI;
          const totalCount  = liveSlots.length + aiSlots.length;

          const liveAvail = availableByClientTrack[`${clientId}-Live`] ?? [];
          const aiAvail   = availableByClientTrack[`${clientId}-AI`]   ?? [];

          const hasLive = liveSlots.length > 0 || liveAvail.length > 0;
          const hasAI   = aiSlots.length  > 0 || aiAvail.length  > 0;

          if (!hasLive && !hasAI) return null;

          const hasBoth = hasLive && hasAI;

          return (
            <div key={clientId} className="sched-client">

              {/* Client header */}
              <div className="sched-client-header">
                <div className="sched-client-initials">{initials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="sched-client-name">{name}</div>
                  <div className="sched-client-count">
                    {totalCount} video{totalCount !== 1 ? 's' : ''} queued
                  </div>
                </div>
              </div>

              {/* Tracks */}
              <div className={`sched-tracks${hasBoth ? ' sched-tracks-two' : ''}`}>
                {hasLive && (
                  <TrackColumn
                    label="Live"
                    slots={liveSlots}
                    available={liveAvail}
                    onDateChange={handleDateChange}
                    onMoveUp={(s) => handleMoveUp(s, liveSlots)}
                    onMoveDown={(s) => handleMoveDown(s, liveSlots)}
                    onRemove={handleRemove}
                    onAdd={(item) => handleAddItem(clientId, 'Live', item)}
                  />
                )}
                {hasAI && (
                  <TrackColumn
                    label="AI"
                    slots={aiSlots}
                    available={aiAvail}
                    onDateChange={handleDateChange}
                    onMoveUp={(s) => handleMoveUp(s, aiSlots)}
                    onMoveDown={(s) => handleMoveDown(s, aiSlots)}
                    onRemove={handleRemove}
                    onAdd={(item) => handleAddItem(clientId, 'AI', item)}
                  />
                )}
              </div>

            </div>
          );
        })}

        {/* Empty state (queue only) */}
        {!loading && view === 'queue' && !error && filteredClientIds.filter(id => {
          const cs = slotsByClient[id] ?? { Live: [], AI: [] };
          const la = availableByClientTrack[`${id}-Live`] ?? [];
          const aa = availableByClientTrack[`${id}-AI`]   ?? [];
          return cs.Live.length > 0 || cs.AI.length > 0 || la.length > 0 || aa.length > 0;
        }).length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13, padding: '60px 0' }}>
            No clients with queued or approved videos
          </div>
        )}

      </div>

      {/* Remove slot confirmation modal */}
      {removeConfirm && (
        <div className="modal-overlay" onClick={() => setRemoveConfirm(null)}>
          <div className="vc-modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
            <div className="vc-header">
              <div><div className="vc-title">Remove from Queue?</div></div>
              <button className="vc-close" onClick={() => setRemoveConfirm(null)}>✕</button>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                Remove <strong style={{ color: 'var(--tx-primary)' }}>
                  {removeConfirm.liveProduction?.script?.scriptName || removeConfirm.aiProduction?.script?.scriptName || 'this slot'}
                </strong> from the posting queue?
              </p>
              <p style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '10px 0 0' }}>
                The production will remain in the pipeline — only the queue slot is removed.
              </p>
            </div>
            <div className="vc-footer">
              <div />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setRemoveConfirm(null)}>Cancel</button>
                <button
                  onClick={() => doRemove(removeConfirm)}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Scheduler Calendar ────────────────────────────────────────────────────────

const CAL_MAX_CHIPS = 3;

function SchedulerCalendar({ slots, clients, clientFilter, onDateChange }) {
  const [viewDate, setViewDate] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [pickerDay,    setPickerDay]    = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [dragOver,     setDragOver]     = useState(null);
  const [peekSlot,     setPeekSlot]     = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  const filteredSlots = clientFilter
    ? slots.filter(s => s.clientId === clientFilter)
    : slots;

  const slotsByDate = useMemo(() => {
    const map = {};
    filteredSlots.forEach(slot => {
      if (!slot.scheduledDate) return;
      const dateStr = typeof slot.scheduledDate === 'string'
        ? slot.scheduledDate.slice(0, 10)
        : new Date(slot.scheduledDate).toISOString().slice(0, 10);
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(slot);
    });
    return map;
  }, [filteredSlots]);

  const unscheduled = useMemo(
    () => filteredSlots.filter(s => !s.scheduledDate),
    [filteredSlots]
  );

  const filteredUnscheduled = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return unscheduled;
    return unscheduled.filter(s => {
      const prod = s.liveProduction ?? s.aiProduction;
      const title = prod?.script?.scriptName ?? '';
      const client = clients.find(c => c.id === s.clientId);
      return title.toLowerCase().includes(q) ||
             (client?.name || '').toLowerCase().includes(q) ||
             (client?.initials || '').toLowerCase().includes(q);
    });
  }, [unscheduled, pickerSearch, clients]);

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const firstDow  = new Date(year, month, 1).getDay();
  const daysInMon = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMon; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  return (
    <div>
      <div className="cal-nav">
        <button className="btn-ghost" style={{ padding: '5px 12px' }} onClick={prevMonth}>‹ Prev</button>
        <span className="cal-month-label">{monthLabel}</span>
        <button className="btn-ghost" style={{ padding: '5px 12px' }} onClick={nextMonth}>Next ›</button>
      </div>

      <div className="cal-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="cal-day-header">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={`pad-${i}`} className="cal-cell-empty" />;
          const mm       = String(month + 1).padStart(2, '0');
          const dd       = String(day).padStart(2, '0');
          const dateStr  = `${year}-${mm}-${dd}`;
          const daySlots = slotsByDate[dateStr] || [];
          const visible  = daySlots.slice(0, CAL_MAX_CHIPS);
          const overflow = daySlots.length - CAL_MAX_CHIPS;
          const isToday  = dateStr === today;
          return (
            <div
              key={dateStr}
              className={`cal-cell${isToday ? ' today' : ''}${dragOver === dateStr ? ' drag-over' : ''}`}
              onClick={() => { setPickerDay(dateStr); setPickerSearch(''); }}
              onDragOver={e => { e.preventDefault(); setDragOver(dateStr); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => {
                e.preventDefault(); setDragOver(null);
                const id = e.dataTransfer.getData('text/plain');
                if (id) {
                  const slot = slots.find(s => s.id === id);
                  if (slot) onDateChange(slot, dateStr);
                }
              }}
            >
              <div className="cal-day-num">{day}</div>
              {visible.map(slot => {
                const prod   = slot.liveProduction ?? slot.aiProduction;
                const title  = slotTitle(slot);
                const client = clients.find(c => c.id === slot.clientId);
                const tc     = slot.track === 'AI' ? 'ai' : 'live';
                return (
                  <div
                    key={slot.id}
                    className={`cal-chip ${tc}`}
                    title={`${title}${client ? ` — ${client.name}` : ''}`}
                    draggable
                    style={{ cursor: 'grab', position: 'relative' }}
                    onDragStart={e => {
                      e.stopPropagation();
                      e.dataTransfer.setData('text/plain', slot.id);
                    }}
                    onDragEnd={() => {}}
                    onClick={e => { e.stopPropagation(); setPeekSlot(slot); }}
                  >
                    <span className="cal-chip-track">{slot.track}</span>
                    <span className="cal-chip-title">{title}</span>
                    <button
                      className="cal-chip-remove"
                      title="Remove from this date"
                      onClick={e => { e.stopPropagation(); onDateChange(slot, null); }}
                    >×</button>
                  </div>
                );
              })}
              {overflow > 0 && (
                <div className="cal-overflow" onClick={e => e.stopPropagation()}>+{overflow} more</div>
              )}
            </div>
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        <div className="cal-unscheduled">
          <div className="cal-unscheduled-title">Unscheduled ({unscheduled.length})</div>
          <div className="cal-unscheduled-chips">
            {unscheduled.map(slot => {
              const prod   = slot.liveProduction ?? slot.aiProduction;
              const title  = slotTitle(slot);
              const client = clients.find(c => c.id === slot.clientId);
              const tc     = slot.track === 'AI' ? 'ai' : 'live';
              return (
                <div
                  key={slot.id}
                  className={`cal-chip ${tc}`}
                  title={client?.name}
                  draggable
                  style={{ maxWidth: 200, cursor: 'grab', position: 'relative' }}
                  onDragStart={e => { e.dataTransfer.setData('text/plain', slot.id); }}
                  onDragEnd={() => {}}
                  onClick={() => setPeekSlot(slot)}
                >
                  <span className="cal-chip-track">{slot.track}</span>
                  <span className="cal-chip-title">{title}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Assign picker */}
      {pickerDay && (
        <div className="modal-overlay" onClick={() => setPickerDay(null)}>
          <div className="cal-assign-popup" onClick={e => e.stopPropagation()}>
            <div className="cal-assign-head">
              <span className="cal-assign-label">Schedule on {pickerDay}</span>
              <button className="vc-close" onClick={() => setPickerDay(null)}>×</button>
            </div>
            <div style={{ padding: '0 14px 10px' }}>
              <input
                autoFocus
                className="form-input"
                style={{ fontSize: 12 }}
                placeholder="Search by title or project…"
                value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
              />
            </div>
            <div className="cal-assign-list">
              {filteredUnscheduled.length === 0 ? (
                <div style={{ padding: '12px 14px', color: 'var(--tx-faint)', fontSize: 12 }}>
                  {unscheduled.length === 0 ? 'All videos are already scheduled.' : 'No matches.'}
                </div>
              ) : filteredUnscheduled.map(slot => {
                const prod   = slot.liveProduction ?? slot.aiProduction;
                const title  = slotTitle(slot);
                const client = clients.find(c => c.id === slot.clientId);
                const tc     = slot.track === 'AI' ? 'ai' : 'live';
                return (
                  <div
                    key={slot.id}
                    className="cal-assign-item"
                    onClick={() => { onDateChange(slot, pickerDay); setPickerDay(null); }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span className={`track-badge ${tc}`}>{slot.track}</span>
                      <span style={{ fontWeight: 500, color: 'var(--tx-primary)', fontSize: 12 }}>{title}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{client?.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Peek popup */}
      {peekSlot && (
        <SlotPeek
          slot={peekSlot}
          clients={clients}
          onDateChange={onDateChange}
          onClose={() => setPeekSlot(null)}
        />
      )}
    </div>
  );
}

function SlotPeek({ slot, clients, onDateChange, onClose }) {
  const prod   = slot.liveProduction ?? slot.aiProduction;
  const title  = slotTitle(slot);
  const client = clients.find(c => c.id === slot.clientId);
  const tc     = slot.track === 'AI' ? 'ai' : 'live';
  const dateDisplay = slot.scheduledDate ? formatDisplayDate(slot.scheduledDate) : 'Unscheduled';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="cal-peek" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <span className={`track-badge ${tc}`}>{slot.track}</span>
          <button className="vc-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 4, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 16 }}>
          {client?.name}{slot.scheduledDate ? ` · ${dateDisplay}` : ' · Unscheduled'}
        </div>
        {slot.scheduledDate && (
          <button
            className="btn-ghost"
            style={{ fontSize: 11, padding: '6px 12px', width: '100%' }}
            onClick={() => { onDateChange(slot, null); onClose(); }}
          >
            Remove Date
          </button>
        )}
      </div>
    </div>
  );
}

// ── Track Column ──────────────────────────────────────────────────────────────

function TrackColumn({ label, slots, available, onDateChange, onMoveUp, onMoveDown, onRemove, onAdd }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [adding,       setAdding]       = useState(false);

  async function handleAdd(item) {
    setAdding(true);
    setShowDropdown(false);
    try {
      await onAdd(item);
    } finally {
      setAdding(false);
    }
  }

  const badgeClass = label === 'Live' ? 'live' : 'ai';

  return (
    <div className="sched-track">

      {/* Track header */}
      <div className="sched-track-header">
        <span className={`track-badge ${badgeClass}`}>{label}</span>
        <span>{slots.length} slot{slots.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Slot list */}
      {slots.length === 0 ? (
        <div className="sched-empty-track">No videos queued</div>
      ) : (
        slots.flatMap((slot, idx) => {
          const production = slot.liveProduction ?? slot.aiProduction;
          const episode    = isEpisodeCard(production);
          const seriesId   = episode ? (production.scriptId || production.script?.id) : null;
          // D-120: episode slots are titled "Ep N — <title>" and sit under ONE series label,
          // emitted before the FIRST slot of that series. Slot order is untouched.
          const title      = episode
            ? `Ep ${production.episodeNumber}${production.episodeTitle ? ` — ${production.episodeTitle}` : ''}`
            : (production?.script?.scriptName ?? `Slot #${slot.slotOrder}`);
          const sameSeries = s => { const p = s.liveProduction ?? s.aiProduction; return isEpisodeCard(p) && (p.scriptId || p.script?.id) === seriesId; };
          const firstOfSeries = episode && !slots.slice(0, idx).some(sameSeries);
          const seriesCount   = firstOfSeries ? slots.filter(sameSeries).length : 0;
          const row = (
            <SlotRow
              key={slot.id}
              num={idx + 1}
              slot={slot}
              title={title}
              nested={episode}
              isFirst={idx === 0}
              isLast={idx === slots.length - 1}
              onDateChange={onDateChange}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onRemove={onRemove}
            />
          );
          return firstOfSeries
            ? [
                <div key={`series:${seriesId}`} className="sched-series-label" data-testid="sched-series">
                  {production.script?.scriptName || '(series)'} · {seriesCount} episode{seriesCount !== 1 ? 's' : ''}
                </div>,
                row,
              ]
            : [row];
        })
      )}

      {/* Add row */}
      <div className="sched-add-row" style={{ position: 'relative' }}>
        <button
          className="sched-add-btn"
          onClick={() => setShowDropdown(v => !v)}
          disabled={adding || available.length === 0}
          title={available.length === 0 ? 'No approved videos available for this track' : 'Add a video to the queue'}
        >
          {adding ? 'Adding…' : available.length === 0 ? 'No videos available' : '+ Add video'}
        </button>

        {showDropdown && available.length > 0 && (
          <>
            {/* Backdrop */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 98 }}
              onClick={() => setShowDropdown(false)}
            />
            <div className="sched-add-dropdown">
              {/* D-120: episode candidates nest under a non-clickable series label */}
              {groupEpisodes(available).flatMap(entry =>
                entry.kind === 'series'
                  ? [
                      <div key={entry.key} className="sched-add-series" data-testid="sched-add-series">
                        {entry.scriptName} · {entry.episodes.length} episode{entry.episodes.length !== 1 ? 's' : ''}
                      </div>,
                      ...entry.episodes.map(item => (
                        <div
                          key={item.id}
                          className="sched-add-item nested"
                          onClick={() => handleAdd(item)}
                        >
                          <div style={{ fontWeight: 500, color: 'var(--tx-primary)' }}>
                            {`Ep ${item.episodeNumber}${item.episodeTitle ? ` — ${item.episodeTitle}` : ''}`}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>
                            {item.client?.name ?? ''}
                          </div>
                        </div>
                      )),
                    ]
                  : [
                      <div
                        key={entry.record.id}
                        className="sched-add-item"
                        onClick={() => handleAdd(entry.record)}
                      >
                        <div style={{ fontWeight: 500, color: 'var(--tx-primary)' }}>
                          {entry.record.script?.scriptName ?? '(No script)'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>
                          {entry.record.client?.name ?? ''}
                        </div>
                      </div>,
                    ]
              )}
            </div>
          </>
        )}
      </div>

    </div>
  );
}

// ── Slot Row ──────────────────────────────────────────────────────────────────

function SlotRow({ num, slot, title, nested = false, isFirst, isLast, onDateChange, onMoveUp, onMoveDown, onRemove }) {
  const [editingDate, setEditingDate] = useState(false);
  const [dateValue,   setDateValue]   = useState(toInputDate(slot.scheduledDate));

  // Keep local date in sync if slot changes from outside
  useEffect(() => {
    setDateValue(toInputDate(slot.scheduledDate));
  }, [slot.scheduledDate]);

  function handleDateBlur() {
    setEditingDate(false);
    onDateChange(slot, dateValue);
  }

  function handleDateKeyDown(e) {
    if (e.key === 'Enter' || e.key === 'Escape') {
      e.target.blur();
    }
  }

  return (
    <div className={`sched-slot${nested ? ' nested' : ''}`}>
      {/* # */}
      <div className="sched-slot-num">#{num}</div>

      {/* Title */}
      <div className="sched-slot-title" title={title}>{title}</div>

      {/* Date — click to edit */}
      <div>
        {editingDate ? (
          <input
            type="date"
            className="sched-slot-date"
            value={dateValue}
            autoFocus
            onChange={e => setDateValue(e.target.value)}
            onBlur={handleDateBlur}
            onKeyDown={handleDateKeyDown}
          />
        ) : (
          <button
            className="sched-slot-date"
            style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
            onClick={() => setEditingDate(true)}
            title="Click to set date"
          >
            {dateValue ? formatDisplayDate(dateValue) : <span style={{ color: 'var(--tx-faint)' }}>Set date…</span>}
          </button>
        )}
      </div>

      {/* Move up */}
      <button
        className="sched-slot-btn"
        onClick={() => onMoveUp(slot)}
        disabled={isFirst}
        title="Move up"
        style={{ opacity: isFirst ? 0.3 : 1 }}
      >
        ↑
      </button>

      {/* Move down */}
      <button
        className="sched-slot-btn"
        onClick={() => onMoveDown(slot)}
        disabled={isLast}
        title="Move down"
        style={{ opacity: isLast ? 0.3 : 1 }}
      >
        ↓
      </button>

      {/* Remove */}
      <button
        className="sched-slot-btn"
        onClick={() => onRemove(slot)}
        title="Remove from queue"
        style={{ color: 'var(--s-incomplete-t)' }}
      >
        ×
      </button>
    </div>
  );
}
